import type * as ld from "launchdarkly-js-client-sdk";
import { LDProvider, useLDClient } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo } from "react";
import { z } from "zod";
import type { Flag } from "@medbillai/utils/flag";
import { FlagContext, type FlagProviderProps, type LDIdentifyFunction, ldLogger, retryIdentify, useFlagContext, useFlagContextSetup } from "./shared";

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
const clientSideId = process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_SIDE_ID;
export const FlagProvider = ({
  children
}: FlagProviderProps) => {
  const {
    isReady,
    startIdentify,
    finishIdentify
  } = useFlagContextSetup();
  return <FlagContext.Provider value={{
    isReady,
    startIdentify,
    finishIdentify
  }} data-sentry-element="FlagContext.Provider" data-sentry-component="FlagProvider" data-sentry-source-file="index.tsx">
      <LDProvider
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    clientSideID={clientSideId ?? ""} options={{
      logger: ldLogger,
      application: {
        id: "medbillai-next"
      }
    }} timeout={3} data-sentry-element="LDProvider" data-sentry-source-file="index.tsx">
        {children}
      </LDProvider>
    </FlagContext.Provider>;
};
export const useLaunchDarklyIdentify = () => {
  const client = useLDClient();
  const {
    startIdentify,
    finishIdentify
  } = useFlagContext();
  const identify = useCallback<LDIdentifyFunction>(async (context: ld.LDContext | ((prevContext?: ld.LDContext) => ld.LDContext)) => {
    if (!client) {
      // This is common on web, where the client is asynchronously
      // initialized, which requires a call to LD. The auth provider includes
      // `identify` in its `useEffect`, so it will automatically retry after
      // the client is available and the identify callback updates.
      return;
    }
    try {
      startIdentify();
      const existingContext = client ? client.getContext() : undefined;
      const newContext = typeof context === "function" ? context(existingContext) : context;
      await retryIdentify(async () => {
        await client.identify(newContext);
      });
    } finally {
      // Need to be defensive here to make sure we always signal readiness
      // upon completion, even if the identify call failed.
      finishIdentify();
    }
  },
  // client doesn't actually change reference as the context updates etc, so
  // this will prevent the callback from ever being recreated.
  [client, startIdentify, finishIdentify]);
  return identify;
};

// On web, we don't support a true live-updating hook in the same way that we do
// on native. This can be improved if/when we switch to the React SDK. For now,
// we just memoize the flag value so that it doesn't continuously re-retrieve it.

/**
 * Hook for retrieving typed feature flags with Zod schema validation.
 *
 * Note: This hook memoizes the schema internally to prevent unnecessary re-renders
 *
 * @param schema - Zod schema for validation
 * @param flag - The feature flag key
 * @param defaultValue - Default value when flag is unavailable or validation fails
 * @returns The typed flag value
 */
export const useTypedVariation = <T,>(schema: z.ZodType<T>, flag: Flag, defaultValue: T): T => {
  const client = useLDClient();

  // biome-ignore lint/correctness/useExhaustiveDependencies: we dont want the developer using this hook to worry about providing a stable reference to the schema to avoid unnecessary re-renders
  const memoizedSchema = useMemo(() => schema, []);
  return useMemo(() => {
    if (!client) {
      return defaultValue;
    }
    const rawValue = client.variation(flag as string, defaultValue) as unknown;
    const parsed = memoizedSchema.safeParse(rawValue);
    if (!parsed.success) {
      console.warn("Type mismatch: expected", JSON.stringify(memoizedSchema), "but got", typeof rawValue, "returning defaultValue:", defaultValue);
      return defaultValue;
    }
    return parsed.data;
  }, [client, memoizedSchema, flag, defaultValue]);
};
export const useBoolVariation = (flag: Flag, defaultValue: boolean) => useTypedVariation(z.boolean(), flag, defaultValue);
export const useStringVariation = (flag: Flag, defaultValue: string) => useTypedVariation(z.string(), flag, defaultValue);
export const useNumberVariation = (flag: Flag, defaultValue: number) => useTypedVariation(z.number(), flag, defaultValue);